import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Column,
  InputTextAreaField,
} from '@tempmee/react';

/**
 * Confirmation modal for the SMS Campaign "Send SMS" flow.
 *
 * The message preview, recipient info and the Cancel / Skip / Confirm actions
 * live in the DS `Dialog`'s own `DialogContent` / `DialogActions` regions, so
 * the buttons always sit in their own footer row below the body instead of
 * overlapping the textarea (GOTU-6459).
 */
export const SendSMSModal = ({
  show,
  smsPhone,
  smsText,
  countText,
  customerNotesText,
  adminNotes,
  errorText,
  showLoader,
  onConfirm,
  onSkip,
  onCancel,
  onChangeText,
  children,
}) => (
  <Dialog open={show} onClose={onCancel} size="medium">
    <DialogTitle onClose={onCancel}>
      <Typography size="heading_3" bold>
        Send SMS
      </Typography>
    </DialogTitle>

    <DialogContent>
      <Column gap={3} py={4}>
        {countText ? (
          <Typography size="body_large" textAlign="center">
            {countText}
          </Typography>
        ) : null}

        {children}

        <Column gap={2}>
          <Typography size="body_large">
            Send sms to:{' '}
            <Typography as="span" size="body_large" bold>
              {smsPhone}
            </Typography>
          </Typography>

          <Typography size="body_large" bold>
            SMS body
          </Typography>
          <InputTextAreaField
            name="smsBody"
            aria-label="SMS body"
            value={smsText || ''}
            height={160}
            onChange={(event) => onChangeText(event.target.value)}
          />

          {customerNotesText ? (
            <Typography size="body_large" bold className="text-error-500">
              Notes: {customerNotesText}
            </Typography>
          ) : null}

          {adminNotes ? (
            <Typography size="body_large" bold className="text-error-500">
              Admin Notes: {adminNotes}
            </Typography>
          ) : null}
        </Column>

        {errorText ? (
          <Typography size="body_large" textAlign="center" className="text-error-500">
            {errorText}
          </Typography>
        ) : null}
      </Column>
    </DialogContent>

    <DialogActions>
      <Button variant="outlined" color="secondary" onClick={onCancel}>
        Cancel
      </Button>
      <Button variant="outlined" color="secondary" onClick={onSkip}>
        Skip
      </Button>
      <Button color="primary" loading={showLoader} onClick={onConfirm}>
        Confirm
      </Button>
    </DialogActions>
  </Dialog>
);

SendSMSModal.propTypes = {
  /** Whether the modal is visible. */
  show: PropTypes.bool,
  /** Recipient phone number for the current message. */
  smsPhone: PropTypes.string,
  /** Editable SMS body for the current message. */
  smsText: PropTypes.string,
  /** Progress label, e.g. "1 / 5". */
  countText: PropTypes.string,
  /** Customer notes shown in red when present. */
  customerNotesText: PropTypes.string,
  /** Admin notes shown in red when present. */
  adminNotes: PropTypes.string,
  /** Error message shown in red when present. */
  errorText: PropTypes.string,
  /** Shows a spinner on the Confirm button while a send is in flight. */
  showLoader: PropTypes.bool,
  /** Send the current message. */
  onConfirm: PropTypes.func.isRequired,
  /** Skip the current recipient without sending. */
  onSkip: PropTypes.func.isRequired,
  /** Cancel the whole campaign (also fired by backdrop / Escape / close). */
  onCancel: PropTypes.func.isRequired,
  /** Called with the edited SMS body text. */
  onChangeText: PropTypes.func.isRequired,
  /** Previous-messages history block rendered above the recipient info. */
  children: PropTypes.node,
};

SendSMSModal.defaultProps = {
  show: false,
  smsPhone: '',
  smsText: '',
  countText: '',
  customerNotesText: '',
  adminNotes: '',
  errorText: '',
  showLoader: false,
  children: null,
};

export default SendSMSModal;
